import * as React from 'react';
import { Component, deps, inject, observer, ComponentProps } from '../../../lib/component';
import { Loading } from '../../loading/loading';
import { classes, style } from 'typestyle';
import { important, percent, px } from 'csx';
import { constantPlayerHeight } from '../../../theme/constant';
import { constantHeaderWithSearchHeight, desktopBreakpoint, desktopContentWidth } from '../config';
import SearchField from '../molecules/Search/SearchField';
import { FilterLayout } from '../molecules/Search/FilterLayout';
import MainWrapper from '../../main-wrapper';
import {
  mediaDesktop,
  mediaLargeDesktop,
  mediaMediumDesktop,
  mediaUpToMediumDesktop,
  mediaWideDesktop,
} from '../utilities/mediaQueries';
import { TrackLine } from '../molecules/TrackLine';
import { TrackSchema } from '../../../types/schema';
import { colorNeutralLight, colorWhite, rebrand } from '../../../theme/color';
import TitleText from '../atoms/TitleText';
import { Button } from '../atoms/button/Button';
import { ButtonSize, ButtonProminence } from '../atoms/button/buttonStyles';
import { ErrorIcon } from '../atoms/icons/ErrorIcon';
import { Return } from '../atoms/icons/Return';
import { SortDropdown } from '../molecules/Search/SortDropdown';
import { LoadMoreArrowButton } from '../atoms/button/LoadMoreArrow';
import ScrollToTopButton from '../atoms/button/ScrollToTopArrowButton';
import { reaction, runInAction } from 'mobx';
import { createRef } from 'react';
import { zSearchTracksOverlay } from '../../../theme/z';
import { spacing } from '../../../theme/spacing';
import { SUPPORT_HUB } from '../../../constants';
import { flexRoot, start, vertical } from 'csstips';
import { ValidatedResults } from '../organisms/search/ValidatedResults';
import { TracksViewType } from '../../../modules/page/search-mvp/model';

const PLAYER_SHOWING_CLASS = 'player-showing';

@inject(deps)
@observer
export class Search extends Component<ComponentProps> {
  private tracksContainer = createRef<HTMLDivElement>();
  private contentWidthObserver: ResizeObserver = null;

  disposer: () => void;

  componentDidMount() {
    // Lock body scroll so that filters and track results are the only scrollable parts of the page
    document.body.classList.add(styles.lockBodyScroll);
    this.setSearchContentWidth();
    this.observeSearchContentWidth();

    // Add reaction to URL changes
    this.disposer = reaction(
      () => this.props.model.router.location.search,
      (search) => {
        // This statement prevents the function from running on leaving the page
        if (this.props.model.router.location.pathname.startsWith('/search')) {
          this.updateSearchStateFromURL(search);
        }
      }
    );
  }

  componentWillUnmount() {
    // Reset body scroll
    document.body.classList.remove(styles.lockBodyScroll);

    if (this.contentWidthObserver) {
      this.contentWidthObserver.disconnect();
    }

    // Clean up the reaction
    if (this.disposer) {
      this.disposer();
    }
  }

  updateSearchStateFromURL(search: string) {
    const {
      controller: {
        page: {
          searchMvp: { performSearch, resetPagination, parseStateFromRouter },
        },
      },
    } = this.props;

    const resetFilters = search.length <= 1;

    parseStateFromRouter(search);
    resetPagination();
    performSearch(resetFilters);
    this.scrollTopTracks();
  }

  scrollTopTracks = () => {
    if (this.tracksContainer?.current) {
      this.tracksContainer.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  /**
   * Sets a CSS variable for --searchContentWidth, making it possible to check the tracks content size exclusive of scrollbars
   *
   * This is used in the CSS to keep the tracks aligned to the centre of the screen on very large screens
   *
   * See https://www.smashingmagazine.com/2023/12/new-css-viewport-units-not-solve-classic-scrollbar-problem/#avoiding-the-classic-scrollbar-problem
   */
  setSearchContentWidth = () => {
    if (this.tracksContainer?.current) {
      const tracksWidth = this.tracksContainer.current.clientWidth;
      document.documentElement.style.setProperty('--searchContentWidth', `${tracksWidth + 360}px`);
    }
  };

  observeSearchContentWidth = () => {
    this.contentWidthObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.setSearchContentWidth();
      });
    });

    if (this.tracksContainer?.current) {
      this.contentWidthObserver.observe(this.tracksContainer.current);
    }
  };

  handleTrackLineClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (this.props.model.page.searchMvp.searchState.query.length > 0) {
      this.props.controller.analytics.sendMixpanel('User clicks link from search result');
    }
    this.props.controller.ui.handleClickLink(event);
  };

  SelectTopSolutionMixPanel = (category: string, selectedTileUrl: string) => {
    this.props.controller.analytics.sendMixpanel('User selects a top result', {
      category,
      selectedTileUrl: `${this.props.model.env.baseUrl}/${selectedTileUrl}`,
    });
  };

  hideFilters = () => {
    runInAction(() => {
      this.props.model.page.searchMvp.openFilter = false;
    });
  };

  render() {
    const {
      model: {
        page: { searchMvp },
        image: { cover },
        ui: { showPlayer, isMobile },
      },
      controller: {
        page: {
          searchMvp: { loadMore, refreshPage },
        },
        analytics: { sendMixpanel },
      },
    } = this.props;

    const resultsTotal = searchMvp.results.length;

    const ShowLoadMore = resultsTotal < searchMvp.pagination.total;

    const searchQuery = this.props.model.router.location.search;

    const searchQueryOrFiltersToCheck = [
      'query=',
      'genre=',
      'mood=',
      'catalogueType=',
      'instrumental=',
      'hideNotAvailableIn=',
      'durationMin=',
      'durationMax=',
      'facebook=',
      'snapchat=',
      'twitch=',
      'twitter=',
      'vimeo=',
      'youtube=',
      'linkedin=',
      'instagram=',
      'TikTok=',
      'podcasting=',
      'tempoMin=',
      'tempoMax=',
    ];

    const searchContainsQueryOrFilter = searchQueryOrFiltersToCheck.some((query) => searchQuery.includes(query));
    const contentAlignmentClassname = searchMvp.isValidatedSearch
      ? styles.contentAlignmentNoFilters
      : styles.contentAlignment;

    const hideTrackResults = searchMvp.tracksView === TracksViewType.HIDDEN;
    const isTrackValidatedSearch = searchMvp.tracksView === TracksViewType.TRACK;

    return (
      <MainWrapper className={styles.container}>
        <SearchField />

        {/* Loader - displays when loading or updating search */}
        {searchMvp.loading && <Loading className={styles.loadingOverlay} />}

        <div className={classes(styles.mainLayout, showPlayer && PLAYER_SHOWING_CLASS)}>
          {!searchMvp.isValidatedSearch && <FilterLayout />}

          {searchMvp.openFilter && !searchMvp.isValidatedSearch && (
            <div className={styles.trackWrapperOverlay} onClick={this.hideFilters}></div>
          )}

          <div
            className={classes(styles.trackWrapper, searchMvp.openFilter && 'filters-open')}
            ref={this.tracksContainer}
          >
            {/* API error handling */}
            {searchMvp.tracksApiError && (
              <div className={styles.contentAlignment}>
                <div className={styles.noResultWrapper} data-test-failed-search>
                  <ErrorIcon size={20} color={colorNeutralLight.toString()} className={styles.errorIcon} />
                  <div className={styles.noResult}>
                    <div className={styles.noResultText}>
                      Sorry, there was a problem retrieving your search results. Please try again or{' '}
                      <a href={SUPPORT_HUB}>submit a support ticket</a>.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error if no results are available for a query */}
            {!searchMvp.isValidatedSearch && searchMvp.noSearchResult && (
              <div className={styles.contentAlignment}>
                <div className={styles.noResultWrapper} data-test-failed-search>
                  <ErrorIcon size={20} color={colorNeutralLight.toString()} />
                  <div className={styles.noResult}>
                    <div className={styles.noResultText}>
                      Sorry, it doesn&apos;t look like we have that at the moment. Please try another search term or
                      start over.
                    </div>
                    <Button
                      data-test-failed-search-button
                      className={styles.noResultbutton}
                      size={ButtonSize.MEDIUM}
                      prominence={ButtonProminence.PRIMARY}
                      onClick={refreshPage}
                    >
                      <span>Start over</span>
                      <Return />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Top results from validated search */}
            {searchMvp.isValidatedSearch && (
              <div className={styles.topSolutionWrapper}>
                <div className={contentAlignmentClassname}>
                  <ValidatedResults />
                </div>
              </div>
            )}

            {searchMvp.results.length > 0 && !hideTrackResults && (
              <>
                {!isTrackValidatedSearch && (
                  <div className={contentAlignmentClassname}>
                    <div className={styles.titleAndPlanWrapper}>
                      <TitleText text="Tracks" className={styles.title} />

                      {!searchMvp.isValidatedSearch && <SortDropdown />}
                    </div>
                  </div>
                )}

                {/* Track results */}
                <div data-test-track-results className={contentAlignmentClassname}>
                  <div className={styles.tracksWrapper}>
                    {searchMvp.results.map((track) => (
                      <TrackLine
                        key={track.identity}
                        track={(track as any) as TrackSchema}
                        onClickLink={this.handleTrackLineClick}
                      />
                    ))}
                  </div>

                  <ScrollToTopButton
                    scrollTop={this.scrollTopTracks}
                    playerIsShowing={showPlayer}
                    isMobile={isMobile}
                    parentEl={this.tracksContainer.current || undefined}
                  />
                </div>

                {/* Pagination and load more */}
                {!isTrackValidatedSearch && (
                  <div className={contentAlignmentClassname}>
                    <div className={classes(styles.loadMoreWrapper, !ShowLoadMore && styles.flexEnd)}>
                      {ShowLoadMore && (
                        <LoadMoreArrowButton
                          hidden={searchMvp.loading}
                          loading={searchMvp.loadingMoreResults}
                          handleLoadMore={() => {
                            loadMore();
                            sendMixpanel('User clicks load more on search track view');
                          }}
                        />
                      )}
                      {searchMvp.pagination.total > 0 && (
                        <span data-test-showing-more>
                          Showing {searchMvp.results.length} of
                          {!searchContainsQueryOrFilter && <span> 1m+</span>}
                          {searchContainsQueryOrFilter &&
                            (searchMvp.pagination.total <= 999 ? (
                              <span> {searchMvp.pagination.total}</span>
                            ) : (
                              <span> 1000+</span>
                            ))}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </MainWrapper>
    );
  }
}

const styles = {
  container: style({
    margin: 0,
    position: 'relative',
    height: '100%',
  }),
  mainLayout: style({
    height: `calc(100dvh - ${constantHeaderWithSearchHeight}px)`,
    overflow: 'hidden',
    display: 'flex',
    position: 'relative',
    $nest: {
      [`&.${PLAYER_SHOWING_CLASS}`]: {
        height: `calc(100dvh - ${constantHeaderWithSearchHeight}px - ${constantPlayerHeight})`,
      },
    },
  }),
  trackWrapper: style(
    vertical,
    {
      position: 'relative',
      height: '100%',
      overflowY: 'auto',
      flex: 1,
      $nest: {
        '&.filters-open': {
          overflowY: 'hidden',
        },
        [`.${PLAYER_SHOWING_CLASS} & > :last-child`]: {
          paddingBottom: spacing.XLARGE,
        },
        '& > *': {
          flexShrink: 0,
        },
      },
    },
    mediaMediumDesktop({
      $nest: {
        '&.filters-open': {
          overflowY: 'auto',
        },
      },
    })
  ),
  trackWrapperOverlay: style(
    {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: rebrand.contrast[90].fade(0.5).toString(),
      zIndex: zSearchTracksOverlay,
    },
    mediaMediumDesktop({
      display: 'none',
    })
  ),
  contentAlignment: style(
    {
      padding: '0 16px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: px(desktopContentWidth + 32),
    },
    mediaUpToMediumDesktop({
      marginLeft: 'auto',
      marginRight: 'auto',
    }),
    mediaDesktop({
      padding: '0 32px',
      maxWidth: px(desktopContentWidth + 64),
    }),
    mediaWideDesktop({
      padding: important('0 48px'),
      maxWidth: important(`${px(desktopContentWidth + 96)}`),
    }),
    mediaLargeDesktop({
      // This code keeps the content aligned with the search field on super wide screens, regardless of scrollbars
      marginRight: `calc((100vw - ${desktopContentWidth + 96}px) / 2 - (100vw - var(--searchContentWidth, 100vw)))`,
      marginLeft: 'auto',
    })
  ),
  contentAlignmentNoFilters: style({
    padding: '0 16px',
    width: '100%',
    maxWidth: px(desktopBreakpoint),
    margin: '0 auto',
  }),
  titleAndPlanWrapper: style({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingTop: '32px',
    justifyContent: 'space-between',
  }),
  tracksWrapper: style({
    paddingTop: '32px',
  }),
  title: style({ paddingRight: px(16) }),
  loadMoreWrapper: style({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '32px 0px',
    width: percent(100),
  }),
  loadMore: style({
    display: 'flex',
    alignItems: 'center',
    color: rebrand.contrast[50].toString(),
    fontWeight: 400,
    fontSize: px(16),
  }),
  loadingOverlay: style({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colorWhite.fade(0.5).toString(),
    zIndex: 25,
  }),
  flexEnd: style({ justifyContent: 'flex-end' }),
  noResultWrapper: style(flexRoot, start, {
    gap: spacing.XSMALL,
    padding: '32px 0px',
    width: percent(100),
  }),
  errorIcon: style({
    flexShrink: 0,
  }),
  noResult: style(flexRoot, vertical, start, {
    gap: spacing.DEFAULT,
  }),
  noResultText: style({
    color: colorNeutralLight.toString(),
    fontWeight: 400,
    fontSize: px(16),
    $nest: {
      '& a': {
        color: colorNeutralLight.toString(),
        textDecoration: 'underline',
      },
    },
  }),
  noResultbutton: style({
    whiteSpace: 'nowrap',
    padding: '4px 8px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  }),
  topSolutionWrapper: style({
    width: percent(100),
    height: 'auto',
    backgroundColor: rebrand.neutralOnDark[10].toString(),
    padding: '32px 0',
    $nest: {
      '&:only-child': {
        flexGrow: 1,
      },
    },
  }),
  lockBodyScroll: style({
    overflowY: important('hidden'),
  }),
};
